import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Check, ArrowRight, X, Sparkle, CircleNotch, CalendarCheck } from "@phosphor-icons/react";
import { BrandMark } from "@/components/Brand";
import useSeo from "@/lib/useSeo";
import { DEMO_BOOKING_URL, DEMO_USES_EXTERNAL } from "@/lib/demoBooking";

// Stripe Payment Links — live mode. Source of truth, do not edit unless you create new links.
// Starter is monthly-only as of 2026-02.
const STRIPE_PAYMENT_LINKS = {
  base_monthly:        "https://buy.stripe.com/fZudR91IUcHL7O03a28IU0a",
  pro_monthly:         "https://buy.stripe.com/bJe28rgDO37b5FSbGy8IU06",
  pro_yearly:          "https://buy.stripe.com/7sY6oH73e6jn4BO4e68IU07",
  enterprise_monthly:  "https://buy.stripe.com/4gMcN5gDOfTXc4gaCu8IU08",
  enterprise_yearly:   "https://buy.stripe.com/3cI8wP9bm8rv4BO7qi8IU09",
};

const FEATURES = {
  free: [
    "Guided product tour with a pre-built sample project",
    "View all 6 lenses with realistic demo data",
    "See an example PMO Weekly Brief",
    "No uploads, no project creation, no AI chat",
  ],
  base: [
    "1 active project (delete & recreate freely)",
    "4 Smart Ingest uploads per month (CSV, Excel, PDF, Word, text)",
    "All 6 lenses · manual add & delete",
    "AI Enrich on any item · AI Bulk Generate (3/month)",
    "5 AI Assistant chat messages per month",
    "Auto-generated Solutions & Insights after each upload",
    "PMO Weekly Brief (in-app)",
  ],
  pro: [
    "20 active projects",
    "40 Smart Ingest uploads per month (10/week)",
    "Everything in Starter, plus:",
    "Unlimited AI Assistant chat with history",
    "Unlimited AI Bulk Generate",
    "PMO Weekly Brief delivered by email + scheduled auto-delivery",
    "Custom industry templates · Priority AI queue",
  ],
  enterprise: [
    "Unlimited projects",
    "Unlimited Smart Ingest uploads",
    "Everything in Pro, plus:",
    "Dedicated weekly brief schedule (multiple recipients)",
    "White-glove onboarding workshop",
    "Priority support · custom integrations on request",
    "SSO and SLA available on request",
  ],
};

const TIER_CARDS = [
  { tier: "base", title: "Starter", subtitle: "For the solo Project Manager", color: "#0020F5", accentBg: "#E5EAFF" },
  { tier: "pro", title: "Pro", subtitle: "Most popular · for PMO leads", color: "#09090B", accentBg: "#FFFFFF", featured: true },
  { tier: "enterprise", title: "Enterprise", subtitle: "Portfolios & multi-team PMOs", color: "#7C3AED", accentBg: "#EDE4FF" },
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [cycle, setCycle] = useState("monthly");
  const [plans, setPlans] = useState({});
  const [loadingId, setLoadingId] = useState(null);

  useSeo({
    title: "Pricing · ARISSAI® · Free, Starter, Pro & Enterprise plans (CAD)",
    description: "ARISSAI® plans in Canadian dollars: Free (3 projects, view-only), Starter $9.99/mo, Pro $79/mo with Weekly Brief & unlimited uploads, Enterprise $199/mo with SSO. Cancel anytime — your data stays.",
    path: "/pricing",
  });

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => setPlans(data.plans || {}));
    if (params.get("cancelled")) toast.info("Checkout cancelled. No charge was made.");
  }, []); // eslint-disable-line

  // Preserve cents when present (so $9.99 doesn't round to $10). Whole-dollar
  // prices ($79, $199) still render without trailing ".00".
  const formatCAD = (amount) => {
    const hasCents = Number(amount) % 1 !== 0;
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0,
    }).format(amount);
  };

  /** Starter is monthly-only — when the user toggles to yearly, fall back to
   *  the monthly plan for Starter so the card never shows "—". */
  const effectiveCycleFor = (tier) => (tier === "base" ? "monthly" : cycle);

  const checkout = async (tier) => {
    if (!user) {
      navigate(`/login?redirect=/pricing`);
      return;
    }
    const planId = `${tier}_${effectiveCycleFor(tier)}`;
    const paymentLink = STRIPE_PAYMENT_LINKS[planId];
    if (!paymentLink) {
      toast.error("This plan is not yet available — please contact hello@arissai.com");
      return;
    }
    setLoadingId(planId);
    try {
      // Pre-fill the buyer's email + tag the Stripe Customer with our internal user_id via
      // client_reference_id so the future webhook can reconcile this purchase to the right account.
      const url = new URL(paymentLink);
      if (user.email) url.searchParams.set("prefilled_email", user.email);
      if (user.id) url.searchParams.set("client_reference_id", user.id);
      window.location.href = url.toString();
    } catch (e) {
      toast.error("Could not open checkout. Please try again or email hello@arissai.com.");
      setLoadingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <BrandMark size={36} testid="pricing-brand" tagline="AI-Driven Project Intelligence" />
          <div className="flex items-center gap-4">
            {user ? (
              <>
                <Link to="/dashboard" className="text-sm font-medium hover:text-zinc-950" data-testid="pricing-dashboard-link">Dashboard</Link>
                <Link to="/account" className="text-sm font-medium hover:text-zinc-950" data-testid="pricing-account-link">Account</Link>
              </>
            ) : (
              <>
                <Link to="/login" className="text-sm font-medium hover:text-zinc-950" data-testid="pricing-login-link">Sign in</Link>
                <Link to="/signup" className="inline-flex items-center px-4 h-10 text-sm font-semibold bg-zinc-950 text-white hover:bg-zinc-800" data-testid="pricing-signup-link">Get started</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-10 py-16 sm:py-20">
        <div className="text-center">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500">// Pricing · CAD</div>
          <h1 className="font-display font-black tracking-tighter text-5xl sm:text-6xl mt-3">
            Project intelligence,<br />priced for every PMO.
          </h1>
          <p className="text-zinc-600 max-w-2xl mx-auto mt-5 text-base leading-relaxed">
            Pay only for what you run through the lenses. All prices in <span className="font-mono">CAD</span>. Cancel anytime — your data stays.
          </p>

          {/* Cycle toggle */}
          <div className="inline-flex items-center bg-white border border-zinc-200 p-1 mt-8" data-testid="cycle-toggle">
            {["monthly", "yearly"].map((c) => (
              <button
                key={c}
                onClick={() => setCycle(c)}
                className={`px-5 h-10 text-sm font-bold uppercase tracking-wider transition-colors ${cycle === c ? "bg-zinc-950 text-white" : "text-zinc-600 hover:text-zinc-950"}`}
                data-testid={`cycle-${c}`}
              >
                {c} {c === "yearly" && <span className="ml-1 text-[10px] text-[#00C7BE]">save 17%</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 mt-12">
          {TIER_CARDS.map((card) => {
            const tierCycle = effectiveCycleFor(card.tier);
            const planId = `${card.tier}_${tierCycle}`;
            const plan = plans[planId];
            const isCurrent = user?.subscription_tier === card.tier;
            // Starter never has a yearly plan — show the monthly-only badge whenever
            // the user has toggled "yearly" so they don't think the card is broken.
            const monthlyOnly = card.tier === "base" && cycle === "yearly";
            return (
              <div
                key={card.tier}
                className={`bg-white p-8 sm:p-10 relative ${card.featured ? "ring-4 ring-zinc-950 z-10" : ""}`}
                data-testid={`tier-card-${card.tier}`}
              >
                {card.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-zinc-950 text-white text-[10px] tracking-[0.2em] uppercase font-bold px-3 py-1">Most popular</div>
                )}
                <div className="w-12 h-12 grid place-items-center" style={{ background: card.accentBg, border: `1px solid ${card.color}30` }}>
                  <Sparkle size={20} weight="fill" style={{ color: card.color }} />
                </div>
                <h3 className="font-display font-black text-3xl mt-5">{card.title}</h3>
                <p className="text-sm text-zinc-600 mt-1">{card.subtitle}</p>

                <div className="mt-6 flex items-baseline gap-2">
                  <span className="font-display font-black text-5xl" data-testid={`tier-price-${card.tier}`}>{plan ? formatCAD(plan.amount) : "—"}</span>
                  <span className="text-zinc-500 text-sm">/ {tierCycle === "yearly" ? "year" : "month"}</span>
                </div>
                <p className="text-xs text-zinc-500 mt-1 font-mono">
                  {plan ? `${plan.currency.toUpperCase()} · billed ${tierCycle}` : ""}
                  {monthlyOnly && (
                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 bg-[#0020F5]/10 text-[#0020F5] text-[10px] tracking-[0.1em] uppercase font-bold border border-[#0020F5]/30" data-testid="starter-monthly-only-badge">
                      Monthly only
                    </span>
                  )}
                </p>

                <button
                  onClick={() => card.tier === "enterprise" ? window.open(DEMO_BOOKING_URL, DEMO_USES_EXTERNAL ? "_blank" : "_self", "noopener,noreferrer") : checkout(card.tier)}
                  disabled={loadingId === planId || isCurrent}
                  className={`mt-7 w-full h-12 inline-flex items-center justify-center gap-2 text-sm font-bold transition-colors disabled:opacity-50 ${card.featured ? "bg-zinc-950 text-white hover:bg-zinc-800" : "border border-zinc-950 hover:bg-zinc-950 hover:text-white"}`}
                  data-testid={`checkout-${card.tier}`}
                >
                  {isCurrent ? "Your current plan" : loadingId === planId ? (
                    <><CircleNotch size={14} weight="bold" className="animate-spin" /> Opening secure checkout…</>
                  ) : card.tier === "enterprise" ? (
                    <><CalendarCheck size={14} weight="bold" /> Book a demo</>
                  ) : <>Choose {card.title} <ArrowRight size={14} weight="bold" /></>}
                </button>
                {card.tier === "enterprise" && (
                  <p className="mt-2 text-[11px] text-zinc-500 leading-snug">
                    Enterprise contracts include SSO, SLA, custom industry packs and procurement support. Self-serve is intentionally disabled — we want to talk to you.
                  </p>
                )}

                <ul className="mt-7 space-y-3">
                  {(FEATURES[card.tier] || []).map((f) => (
                    <li key={`${card.tier}-${f}`} className="flex items-start gap-2 text-sm text-zinc-800">
                      <Check size={16} weight="bold" className="shrink-0 mt-0.5" style={{ color: card.color }} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Free tier comparison strip */}
        <div className="mt-10 bg-white border border-zinc-200 p-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Free · No card required</div>
            <div className="font-display font-bold text-xl mt-1">Take the guided demo tour</div>
            <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
              Explore a pre-built sample project across all six lenses. See exactly how ARISSAI works before you commit a dollar — no uploads, no AI generation, no card.
            </p>
          </div>
          {!user ? (
            <Link to="/signup" className="inline-flex items-center gap-2 h-11 px-5 border border-zinc-950 text-sm font-bold hover:bg-zinc-950 hover:text-white" data-testid="cta-free">
              Start the demo <ArrowRight size={14} weight="bold" />
            </Link>
          ) : (
            <Link to="/dashboard" className="inline-flex items-center gap-2 h-11 px-5 border border-zinc-950 text-sm font-bold hover:bg-zinc-950 hover:text-white" data-testid="cta-dashboard-from-pricing">
              Go to dashboard <ArrowRight size={14} weight="bold" />
            </Link>
          )}
        </div>

        <p className="text-center text-xs text-zinc-500 mt-10 max-w-2xl mx-auto">
          All prices in <span className="font-mono">CAD</span>. Cancel anytime — your data stays. Need SSO, SLA, or custom contracting? <a href="mailto:sales@arissai.ca" className="underline hover:text-zinc-950">Email sales</a>.
        </p>
      </main>
    </div>
  );
}
